/**
 * This is a SwatchDropDownItem Component.This Component is used to render the swatch dropdown item.
 *
 * @module views/Molecules/SwatchDropDownItem
 * @memberof -Common
 */
import './SwatchDropDownItem.scss';

import React, { useCallback, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { Formik } from 'formik';
import PropTypes from 'prop-types';

import Text from '@ulta/core/components/Text/Text';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import { hasItems } from '@ulta/core/utils/array/array';

import Tag from '@ulta/modules/Tag/Tag';

import { constants } from '@ulta/utils/constants/constants';

import Swatch from '../Swatch/Swatch';
import * as utils from './SwatchDropDownItem';

const { ENTER_KEY, ESC_KEY, UPARROW_KEY, DOWNARROW_KEY, TAB_KEY } = constants;
/**
  * Represents a SwatchDropDownItem component
  *
  * @method
  * @param {SwatchDropDownItemProps} props - React properties passed from composition
  * @returns SwatchDropDownItem
  */

export const SwatchDropDownItem = ( props )=>{
  const {
    activeCheck,
    actElm,
    altText,
    onChange,
    setListOpen,
    setActElm,
    headerRef,
    itemLength,
    index,
    image,
    selected = defaultProps.selected,
    disabled = defaultProps.disabled,
    name,
    shadeDescription,
    unavailable,
    tags
  } = props;
  const swatchItemRef = useRef();

  useEffect( () => {
    if( activeCheck ){
      swatchItemRef.current?.focus();
    }
  }, [actElm] );

  UseKeyPress( [ESC_KEY, TAB_KEY, ENTER_KEY, UPARROW_KEY, DOWNARROW_KEY], swatchItemRef, ( ev )=>{
    utils.composeKeyPress( { keyCode: ev.keyCode, headerRef, actElm, itemLength }, { onChange, setActElm, setListOpen } );
  }, [TAB_KEY] );

  const handleActElm = useCallback( () => {
    setActElm( index );
  }, [index, setActElm] );

  const hasTags =  hasItems( tags );

  return (
    <Formik
      initialValues={ {} }
    >
      { () => (
        <div className={ classNames( 'SwatchDropDownItem', {
          'SwatchDropDownItem__active': activeCheck
        } ) }
        role='option'
        id={ `option-${ index + 1 }` }
        aria-selected={ activeCheck }
        tabIndex='0'
        ref={ swatchItemRef }
        onKeyPress={ handleActElm }
        onClick={ onChange }
        >
          <div>
            <input className='SwatchDropDownItem_input'
              name={ name }
              value={ name }
              id={ name }
              type={ 'radio' }
              onChange={ onChange }
              tabIndex={ -1 }
            />
            <label
              htmlFor={ name }
              id={ name }
            >
              <Swatch
                src={ image }
                alt={ altText }
                height={ 40 }
                width={ 40 }
                selected={ selected }
                disabled={ disabled }
                unavailable={ unavailable }
              />

              { name &&
              <Text textStyle='body-3'
                htmlTag='span'
              >{ name }</Text>
              }
              { shadeDescription &&
              <Text color='neutral-700'
                textStyle='body-3'
                htmlTag='span'
              >{ ` (${ shadeDescription })` }</Text>
              }
            </label>
          </div>
          { hasTags && (
            <div className='SwatchDropDownItem__tag'>
              { tags?.map( ( tag, index ) => (
                <Tag
                  key={ `${index}-${tag.message}` }
                  boldText={ tag.message }
                  size={ tag.size }
                  variant={ tag.variant }
                  backgroundColor={ tag.backgroundColor }
                  textColor={ tag.textColor }
                />
              ) ) }
            </div> ) }
        </div>
      ) }
    </Formik>
  );
};

/**
 *
 * @method composeKeyPress method to handle keyEvents/key presses
 * @param {string|array} keyCode - the code of the key to respond to, compared against event.keyCode
 * @param {number} itemLength - list item length/count
 * @param {number} actElm - return index as state value
 * @param {object} headerRef - headerRef
 * @param {function} setListOpen - state update method
 * @param {function} onChange - method to call when item is clicked.
 * @param {function} setActElm - useState setter for list active element
*/
export const composeKeyPress = ( data, methods ) => {
  const { keyCode, headerRef, itemLength, actElm } = data || {};
  const { setListOpen, onChange, setActElm } = methods || {};

  if( !keyCode ){
    return null;
  }

  switch ( keyCode ){
    case ENTER_KEY: onChange(); break;
    case TAB_KEY: utils.swatchDropDownClose( { headerRef }, { setListOpen } ); break;
    case ESC_KEY: utils.swatchDropDownClose( { headerRef }, { setListOpen } ); break;
    case UPARROW_KEY: setActElm( actElm === 0 ? itemLength - 1 : actElm - 1 ); break;
    case DOWNARROW_KEY: setActElm( actElm === itemLength - 1 ? 0 : actElm + 1 ); break;
  }
};

/**
 *
 * @method swatchDropDownClose method to handle close the Dropdown and return the focus to selector div
 * @param {object} headerRef - headerRef to return focus on ESC_KEY
 * @param {method} setListOpen - state update method
*/
export const swatchDropDownClose = ( data, methods ) => {
  const { headerRef } = data || {};
  const { setListOpen } = methods || {};

  if( !setListOpen ){
    return null;
  }

  setListOpen( false );
  headerRef.current.focus();
};

/**
  * Property type definitions
  * @typedef SwatchDropDownItemProps
  * @type {object}
  * @property {string} name - dropdown item name
  * @property {string} shadeDescription - the shade description for the swatch.
  * @property {boolean} selected - whether or not the item is selected
  * @property {string} image - the dropdown item image source
  * @property {string} altText - altText text of the image for ADA
  * @property {boolean} disabled - whether or not the item is disabled (i.e. out of stock)
  * @property {function} onChange - method to call when item is clicked.
  * @property {boolean} activeCheck - return true/false if activeElement and index are same in dropdown
  * @property {function} setListOpen - useState setter for list open/close boolean
  * @property {function} setActElm - useState setter for list active element
  * @property {number} index - list index value
  * @property {number} itemLength - list item length/count
  * @property {boolean} unavailable - Availability state
  */

export const propTypes =  {
  name: PropTypes.string.isRequired,
  shadeDescription: PropTypes.string,
  selected: PropTypes.bool,
  image: PropTypes.string.isRequired,
  altText: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  activeCheck: PropTypes.bool,
  setListOpen: PropTypes.func,
  setActElm: PropTypes.func,
  index: PropTypes.number,
  itemLength: PropTypes.number,
  unavailable: PropTypes.bool
};

/**
  * Default values for passed properties
  * @type {object}
  * @property {boolean} selected=false - by default swatch is not selected
  * @property {boolean} disabled=false - by default swatch is not disabled
  */
export const defaultProps = {
  selected: false,
  disabled: false
};

SwatchDropDownItem.propTypes = propTypes;
SwatchDropDownItem.defaultProps = defaultProps;

export default SwatchDropDownItem;